// components/PreviewFooter.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../middleware/ThemeContext';
import { TempSettings, MenuItem } from '../../types/types';
import { Theme } from '../../utils/themes';
import StyledText from '../StyledComponents/StyledText';
import FacebookIcon from '../../assets/icons/FacebookIcon';
import SqaureLinkedInIcon from '../../assets/icons/SqaureLinkedInIcon';
import NewYoutubeIcon from '../../assets/icons/NewYoutubeIcon';
import NewXIcon from '../../assets/icons/NewXIcon';

const FooterContainer = styled.footer<{ $isHovered?: boolean; theme: Theme; $backgroundColor?: string }>`
  background: ${({ $backgroundColor }) => $backgroundColor || 'white'};
  border-top: 1px solid #e9ecef;
  padding: 30px;
  margin-top: auto;
  border-radius: 0px 0px 10px 10px;
  box-shadow: 0px -4px 40px 0px #0000001A;
  position: relative;
  transition: all 0.3s;
  border: ${props => props.$isHovered ? `2px solid ${props.theme.CorporateTheme.colors.blueberry}` : '2px solid transparent'};
  
  &:hover {
    box-shadow: 0px -4px 40px 0px rgba(108, 99, 255, 0.2);
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 1fr;
  gap: 40px;
  max-width: 1400px;
  margin: 0 auto;
  align-items: start;
`;

const FooterBrand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 300px;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 4px;
`;

const SocialIconLink = styled.a`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MenuLinksContainer = styled.div`
  display: flex;
  gap: 40px;
`;

const MenuColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AddressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FooterLink = styled.a<{ $textColor?: string; $primaryFont?: string }>`
  color: ${({ $textColor }) => $textColor || '#fff'};
  text-decoration: none;
  font-size: 14px;
  font-weight: 400;
  font-family: ${({ $primaryFont }) => $primaryFont || 'inherit'};
  transition: opacity 0.2s;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const FooterBottom = styled.div<{ theme: Theme }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.border};
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

interface PreviewFooterProps {
  settings: TempSettings;
  menuItems?: MenuItem[];
  primaryFont?: string;
}

const PreviewFooter: React.FC<PreviewFooterProps> = ({ settings, menuItems = [], primaryFont }) => {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const appliedSettings = settings || {};
  const currentYear = new Date().getFullYear();

  const footerBackgroundColor = appliedSettings.backgroundColor || 'white';
  const footerTextColor = appliedSettings.textColor || theme.CorporateTheme.colors.textSecondary;
  const footerCopyrightText =
    appliedSettings.copyrightText?.trim() ||
    `Copyright © ${currentYear}. All Rights Reserved.`;
  const hasPrivacyPolicyLink =
    Boolean(appliedSettings.showPrivacyPolicy) &&
    Boolean(appliedSettings.privacyPolicy?.trim());
  const hasTermsLink =
    Boolean(appliedSettings.showTermsAndConditions) &&
    Boolean(appliedSettings.termOfService?.trim());

  return (
    <FooterContainer 
      theme={theme}
      $isHovered={isHovered}
      $backgroundColor={footerBackgroundColor}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <FooterContent>
        {/* Brand Section */}
        <FooterBrand>
          <div className='d-flex align-items-center gap-2'>
            <div>
              {appliedSettings.websiteLogo ? (
                <img
                  src={appliedSettings.websiteLogo}
                  alt="Website Logo"
                  style={{ height: '40px', width: 'auto', objectFit: 'contain' }}
                />
              ) : null}
            </div>
          </div>
          {appliedSettings.content && (
            <StyledText lineHeight='1.5' fontSize='14px' fontWeight={400} textColor={footerTextColor} style={{ fontFamily: primaryFont || 'inherit' }}>
              {appliedSettings.content}
            </StyledText>
          )}
          
          {/* Social Media Icons */}
          <SocialIcons>
            {appliedSettings.facebookUrl && appliedSettings.facebookUrl.trim() && (
              <SocialIconLink href={appliedSettings.facebookUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <FacebookIcon  />
              </SocialIconLink>
            )}
            {appliedSettings.linkedinUrl && appliedSettings.linkedinUrl.trim() && (
              <SocialIconLink href={appliedSettings.linkedinUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <SqaureLinkedInIcon  />
              </SocialIconLink>
            )}
            {appliedSettings.youtubeUrl && appliedSettings.youtubeUrl.trim() && (
              <SocialIconLink href={appliedSettings.youtubeUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <NewYoutubeIcon  />
              </SocialIconLink>
            )}
            {appliedSettings.twitterUrl && appliedSettings.twitterUrl.trim() && (
              <SocialIconLink href={appliedSettings.twitterUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                <NewXIcon />
              </SocialIconLink>
            )}
          </SocialIcons>
        </FooterBrand>

        {/* Menu Links */}
        <div>
          {menuItems.length > 0 ? (
            menuItems.length <= 5 ? (
              <MenuColumn>
                {menuItems.map((item) => (
                  <FooterLink key={item.id} $textColor={footerTextColor} $primaryFont={primaryFont} href={item.page.url} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                    {item.page.name}
                  </FooterLink>
                ))}
              </MenuColumn>
            ) : (
              <MenuLinksContainer>
                <MenuColumn>
                  {menuItems.slice(0, 5).map((item) => (
                    <FooterLink key={item.id} $textColor={footerTextColor} $primaryFont={primaryFont} href={item.page.url} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      {item.page.name}
                    </FooterLink>
                  ))}
                </MenuColumn>
                <MenuColumn>
                  {menuItems.slice(5).map((item) => (
                    <FooterLink key={item.id} $textColor={footerTextColor} $primaryFont={primaryFont} href={item.page.url} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      {item.page.name}
                    </FooterLink>
                  ))}
                </MenuColumn>
              </MenuLinksContainer>
            )
          ) : null}
        </div>

        {/* Address */}
        <AddressSection>
          <StyledText lineHeight='1.6' fontSize='14px' fontWeight={400} textColor={footerTextColor} style={{ fontFamily: primaryFont || 'inherit' }}>
            {appliedSettings.address || ''}
          </StyledText>
        </AddressSection>
      </FooterContent>
      
      <FooterBottom theme={theme}>
        <StyledText fontSize='14px' fontWeight={400} textColor={footerTextColor} style={{ fontFamily: primaryFont || 'inherit' }}>
          {footerCopyrightText}
        </StyledText>
        
        {(hasPrivacyPolicyLink || hasTermsLink) && (
          <FooterBottomLinks>
            {hasPrivacyPolicyLink && (
              <FooterLink $textColor={footerTextColor} $primaryFont={primaryFont} href={appliedSettings.privacyPolicy} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Privacy Policy</FooterLink>
            )}
            {hasTermsLink && (
              <FooterLink $textColor={footerTextColor} $primaryFont={primaryFont} href={appliedSettings.termOfService} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>Terms & Conditions</FooterLink>
            )}
          </FooterBottomLinks>
        )}
      </FooterBottom>
    </FooterContainer>
  );
};

export default PreviewFooter;
